import {
  type DesignComposerOperation,
  type DesignComposerTargetPicker,
  searchDesignSystemMentionTargets,
} from "@afar-research/afar";
import type { DesignSystemMentionTarget } from "@afar-research/contracts";
import { memo, useState } from "react";
import { CheckIcon, ChevronDownIcon, PaletteIcon, SearchIcon } from "lucide-react";

import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { DESIGN_MODE_TINT as MODE_TINT } from "./designOperationVoice";
import { useComposerMenuProps } from "./composerEventScope";
import { cn } from "~/lib/utils";

/**
 * The composer's Design System picker, in the footer beside the paperclip.
 *
 * The target is the precondition five of the seven Design operations share, and
 * it used to be reachable only by typing `@` — a shortcut the placeholder had
 * to teach in every one of those modes. This is the control that pick belongs
 * to; `@` still works and lands in the same place, because both routes go
 * through `selectDesignComposerMentions`.
 *
 * Delete is the one mode this does not resolve itself: its selection spans
 * Design Systems *and* the Design Artifact Groups inside them, with containment
 * between them, which is a tree rather than a list. Its trigger opens
 * `DesignDeleteTargetsDialog` and wears the count that dialog came back with.
 *
 * Open state is the caller's, because opening is what puts the project's
 * Design Systems on the wire. A control that owned it would open on whatever
 * the composer had already fetched for another reason — which, in the state
 * this control exists for, is nothing.
 */
export const DesignTargetPicker = memo(function DesignTargetPicker(props: {
  /** `null`, or a mode that collects nothing, renders nothing. */
  picker: DesignComposerTargetPicker | null;
  mode: DesignComposerOperation;
  targets: ReadonlyArray<DesignSystemMentionTarget>;
  /** The picked target's path, so the open list can mark it. */
  selectedPath: string | null;
  /**
   * Whether the listing is still on its way. An empty list is only empty once
   * this is false: saying a project has no Design System while its listing is
   * in flight tells the user to create one they already have.
   */
  isLoading: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
  onPickTarget: (target: DesignSystemMentionTarget) => void;
  onOpenDeleteTargets: () => void;
}) {
  const [query, setQuery] = useState("");
  // Above the early return: the control unmounts its popover in Create and in
  // an unpicked composer, and a hook called below that would not survive it.
  const composerFloatingLayerProps = useComposerMenuProps();
  const picker = props.picker;
  if (picker === null) return null;

  const tint = MODE_TINT[props.mode];
  const triggerClassName = cn(
    "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs transition-colors focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50",
    picker.picked
      ? cn(tint.text, tint.surface)
      : "text-muted-foreground hover:bg-accent hover:text-foreground",
  );

  if (picker.kind === "multi") {
    return (
      <button
        aria-label={`Design Systems and artifacts to delete: ${picker.label}`}
        className={triggerClassName}
        disabled={props.disabled}
        onClick={props.onOpenDeleteTargets}
        title="Choose the Design Systems and artifact groups to remove"
        type="button"
      >
        <PaletteIcon aria-hidden="true" className="size-3.5" />
        <span className="max-w-32 truncate">{picker.label}</span>
      </button>
    );
  }

  const options = searchDesignSystemMentionTargets(props.targets, query);
  return (
    <Popover
      open={props.open}
      onOpenChange={(next) => {
        props.onOpenChange(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger
        aria-label={`Design System: ${picker.label}`}
        className={triggerClassName}
        disabled={props.disabled}
      >
        <PaletteIcon aria-hidden="true" className="size-3.5" />
        <span className="max-w-32 truncate">{picker.label}</span>
        <ChevronDownIcon aria-hidden="true" className="size-3" />
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        aria-label="Choose a Design System"
        className="w-80 max-w-[calc(100vw-2rem)] p-0"
        side="top"
        viewportClassName="p-0 [--viewport-inline-padding:0px]"
        {...composerFloatingLayerProps}
      >
        <label className="flex items-center gap-2 border-b border-border px-3 py-2">
          <SearchIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          <input
            aria-label="Search Design Systems"
            autoFocus
            className="w-full bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search Design Systems…"
            value={query}
          />
        </label>
        <div className="max-h-72 overflow-y-auto p-1">
          {options.length === 0 ? (
            <p className="px-3 py-6 text-center text-[13px] text-muted-foreground">
              {props.isLoading
                ? "Reading this project's Design Systems…"
                : props.targets.length === 0
                  ? "This project has no Design System yet. Create one with the Create operation."
                  : `No Design System matches “${query}”.`}
            </p>
          ) : (
            options.map((target) => {
              const selected = target.path === props.selectedPath;
              return (
                <button
                  aria-selected={selected}
                  className={cn(
                    "flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors",
                    selected ? cn(tint.surface, tint.text) : "hover:bg-muted/60",
                  )}
                  key={target.path}
                  onClick={() => {
                    props.onPickTarget(target);
                    props.onOpenChange(false);
                  }}
                  role="option"
                  type="button"
                >
                  <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
                    {selected ? <CheckIcon aria-hidden="true" className="size-3.5" /> : null}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-medium">{target.name}</span>
                    {target.description === null ? null : (
                      <span
                        className={cn(
                          "block text-[12px] leading-snug",
                          selected ? "opacity-80" : "text-muted-foreground",
                        )}
                      >
                        {target.description}
                      </span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverPopup>
    </Popover>
  );
});
